import { useCallback, useEffect, useRef, useState } from "react"

import { z } from "zod"

import { fetchSofiaProxy } from "@/lib/sofiaProxyRequest"

import {
  githubBindingResponseSchema,
  githubBindingsSchema,
  githubBrowserCallbackSchema,
  githubRedirectSchema,
  type GithubBinding,
  type GithubSelection,
} from "../../utils/githubConnectionSchemas"

export function useGithubConnections(
  organizationSlug: string,
  workspaceId: string,
  enabled: boolean,
) {
  const [bindings, setBindings] = useState<GithubBinding[]>([])
  const [selection, setSelection] = useState<GithubSelection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const mounted = useRef(false)
  const writing = useRef(false)
  const controller = useRef<AbortController | null>(null)
  const reloadGeneration = useRef(0)
  const selectionGeneration = useRef(0)

  const request = useCallback(
    async <T>(
      action: string,
      schema: z.ZodType<T>,
      body?: Record<string, unknown>,
      method?: string,
    ) => {
      controller.current?.abort()
      const active = new AbortController()
      controller.current = active
      const url = `/api/sofia/github/connections/${encodeURIComponent(organizationSlug)}/${action}`
      const response = await fetchSofiaProxy(
        body ? url : `${url}?workspace=${encodeURIComponent(workspaceId)}`,
        {
          organizationSlug,
          method: method ?? (body ? "POST" : "GET"),
          headers: body ? { "Content-Type": "application/json" } : undefined,
          body: body
            ? JSON.stringify({ workspace: workspaceId, ...body })
            : undefined,
          signal: active.signal,
        },
      )
      if (!mounted.current || active.signal.aborted) {
        throw new DOMException("Aborted", "AbortError")
      }
      let payload: unknown
      try {
        payload = await response.json()
      } catch {
        payload = undefined
      }
      if (!mounted.current || active.signal.aborted) {
        throw new DOMException("Aborted", "AbortError")
      }
      if (!response.ok) {
        const parsed = z.object({ error: z.string() }).safeParse(payload)
        throw new Error(
          parsed.success
            ? parsed.data.error
            : "Could not manage GitHub connections. Try again.",
        )
      }
      const parsed = schema.safeParse(payload)
      if (!parsed.success) {
        throw new Error(
          "GitHub returned an invalid response. Refresh and try again.",
        )
      }
      return parsed.data
    },
    [organizationSlug, workspaceId],
  )

  const reload = useCallback(
    async (options?: { preserveError?: boolean }) => {
      if (!enabled || writing.current) {
        return
      }
      const generation = ++reloadGeneration.current
      setLoading(true)
      if (!options?.preserveError) {
        setError(null)
      }
      try {
        const data = await request("list", githubBindingsSchema)
        if (generation !== reloadGeneration.current) {
          return
        }
        if (data.workspace_id !== workspaceId) {
          throw new Error("Connection workspace did not match.")
        }
        setBindings(data.bindings)
      } catch (cause) {
        if (
          cause instanceof Error &&
          cause.name !== "AbortError" &&
          mounted.current &&
          generation === reloadGeneration.current
        ) {
          setBindings([])
          selectionGeneration.current += 1
          setSelection(null)
          setError(cause.message)
        }
      } finally {
        if (mounted.current && generation === reloadGeneration.current) {
          setLoading(false)
        }
      }
    },
    [enabled, request, workspaceId],
  )

  useEffect(() => {
    mounted.current = true
    void reload()
    return () => {
      mounted.current = false
      controller.current?.abort()
    }
  }, [reload])

  const mutate = async (operation: () => Promise<void>): Promise<boolean> => {
    if (!enabled || writing.current) {
      return false
    }
    writing.current = true
    setBusy(true)
    setError(null)
    try {
      await operation()
      return true
    } catch (cause) {
      if (
        cause instanceof Error &&
        cause.name !== "AbortError" &&
        mounted.current
      ) {
        setError(cause.message)
      }
      return false
    } finally {
      writing.current = false
      if (mounted.current) {
        setBusy(false)
      }
    }
  }

  const clearSelection = useCallback(() => {
    selectionGeneration.current += 1
    setSelection(null)
  }, [])

  return {
    bindings,
    selection,
    error,
    loading,
    busy,
    reload,
    clearSelection,
    connect: (
      agentId: string,
      binding?: GithubBinding,
      options?: { installationHint?: number; newInstallation?: boolean },
    ) =>
      mutate(async () => {
        const installationId = options?.newInstallation
          ? undefined
          : (options?.installationHint ?? binding?.installation.installation_id)
        const data = await request("start", githubRedirectSchema, {
          agent_id: agentId,
          expected_binding_revision: binding?.binding_revision ?? null,
          ...(installationId ? { installation_id: installationId } : {}),
        })
        window.location.assign(data.authorization_url)
      }),
    continueCallback: (agentId: string, onSelection: () => void) =>
      mutate(async () => {
        const generation = selectionGeneration.current
        const data = await request("callback", githubBrowserCallbackSchema, {})
        if (data.status === "redirect") {
          window.location.assign(data.authorization_url)
          return
        }
        if (data.agent_id !== agentId) {
          throw new Error(
            "Choose the agent that started this connection, then reconnect.",
          )
        }
        if (generation !== selectionGeneration.current) {
          return
        }
        setSelection(data)
        onSelection()
      }),
    select: (repositoryId: number, onBound?: () => void) =>
      mutate(async () => {
        if (!selection) {
          throw new Error("Choose a repository after connecting GitHub.")
        }
        const { binding } = await request(
          "select",
          githubBindingResponseSchema,
          {
            selection_id: selection.selection_id,
            repository_id: repositoryId,
            expected_binding_revision: selection.expected_binding_revision,
          },
        )
        if (
          binding.workspace_id !== workspaceId ||
          binding.agent_id !== selection.agent_id ||
          binding.repository.repository_id !== repositoryId
        ) {
          throw new Error(
            "Repository binding did not match this selection. Refresh to check its status.",
          )
        }
        setBindings((current) => [
          ...current.filter((value) => value.agent_id !== binding.agent_id),
          binding,
        ])
        setSelection(null)
        onBound?.()
      }),
    disconnect: (binding: GithubBinding) =>
      mutate(async () => {
        const result = await request(
          "disconnect",
          githubBindingResponseSchema,
          {
            agent_id: binding.agent_id,
            expected_binding_revision: binding.binding_revision,
          },
          "DELETE",
        )
        setBindings((current) => [
          ...current.filter((value) => value.agent_id !== binding.agent_id),
          result.binding,
        ])
        setSelection(null)
      }).then((disconnected) => {
        if (!disconnected && mounted.current) {
          void reload({ preserveError: true })
        }
      }),
  }
}
